import React from 'react';
import { ColumnsType, ColumnType } from 'antd/es/table';
import { GenerationStatus } from '../../../constants/status';
import { statusColorGeneration } from '../../../constants/statusColors';
import { CustomProduct, CustomProductInput } from '../../../store/customProductSlice';
import { Button, Tag, Tooltip, Typography } from 'antd';

interface ColumnsProps {
    getColumnSearchProps: (dataIndex: string, customRender?: any) => any;
    handleProductDetailModalOpen: (product: CustomProduct) => void;
    marketplaceFilterProps: ColumnType<CustomProduct>;
    brandFilterProps: ColumnType<CustomProduct>;
    generationStatusFilters: { text: string; value: GenerationStatus }[];
}

const inputCompletenessFields: Array<{ key: keyof CustomProductInput; label: string }> = [
    { key: 'productName', label: 'Product name' },
    { key: 'brand', label: 'Brand' },
    { key: 'category', label: 'Category' },
    { key: 'marketplace', label: 'Marketplace' },
    { key: 'brandWebsite', label: 'Brand website' },
    { key: 'productContents', label: 'Product contents' },
    { key: 'uniqueSellingProposition', label: 'USP' },
    { key: 'compliance', label: 'Compliance' },
    { key: 'targetAudience', label: 'Target audience' },
    { key: 'language', label: 'Language' },
    { key: 'brandDescription', label: 'Brand description' },
    { key: 'variation', label: 'Variation' },
    { key: 'keyBenefit', label: 'Key benefit' },
    { key: 'technicalSpec', label: 'Technical spec' },
];

const keywordFields: Array<keyof CustomProductInput> = [
    'globalMustHaveKeywords',
    'titleHighPriorityKeywords',
    'titleOptionalKeywords',
    'bulletPointHighPriorityKeywords',
    'bulletPointOptionalKeywords',
    'descriptionHighPriorityKeywords',
    'descriptionOptionalKeywords',
    'negativeKeywords',
];

const hasValue = (value: unknown): boolean => {
    if (Array.isArray(value)) return value.length > 0;
    if (value && typeof value === 'object') return Object.keys(value).length > 0;
    return value !== null && value !== undefined && String(value).trim() !== '';
};

const getInputCompleteness = (input: CustomProductInput = {}) => {
    const missing = inputCompletenessFields
        .filter(({ key }) => !hasValue(input[key]))
        .map(({ label }) => label);

    return {
        filled: inputCompletenessFields.length - missing.length,
        total: inputCompletenessFields.length,
        missing,
    };
};

const getKeywordCount = (input: CustomProductInput = {}) => (
    keywordFields.reduce((count, key) => {
        const value = input[key];
        return count + (Array.isArray(value) ? value.length : 0);
    }, 0)
);

const formatDateTime = (value: any): string => {
    if (!value) return '-';
    const date = typeof value.toDate === 'function'
        ? value.toDate()
        : value.seconds
            ? new Date(value.seconds * 1000)
            : new Date(value);

    if (Number.isNaN(date.getTime())) return '-';

    return new Intl.DateTimeFormat('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
};

const timestampMs = (value: any): number => {
    if (!value) return 0;
    if (typeof value.toMillis === 'function') return value.toMillis();
    if (typeof value.toDate === 'function') return value.toDate().getTime();
    if (value.seconds) return value.seconds * 1000;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

export const getColumns = ({
    getColumnSearchProps,
    handleProductDetailModalOpen,
    marketplaceFilterProps,
    brandFilterProps,
    generationStatusFilters
}: ColumnsProps): ColumnsType<CustomProduct> => [
        {
            title: 'Internal ID',
            dataIndex: 'internalId',
            key: 'internalId',
            width: 110,
            fixed: 'left',
            ...getColumnSearchProps(
                'internalId',
                (highlightedText: string, product: CustomProduct) => (
                    <Tooltip title="Show details" >
                        <Button
                            type="link"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleProductDetailModalOpen(product);
                            }}
                            style={{ height: 'auto', padding: 0 }}
                        >
                            {highlightedText}
                        </Button>
                    </Tooltip>
                )
            )
        },
        {
            title: 'Marketplace',
            dataIndex: ['input', 'marketplace'],
            key: 'marketplace',
            width: 114,
            fixed: 'left',
            ...marketplaceFilterProps,
        },
        {
            title: 'Product Name',
            dataIndex: ['input', 'productName'],
            key: 'productName',
            width: 620,
            ellipsis: true,
            render: (value: string) => (
                <Tooltip title={value} mouseEnterDelay={0.35}>
                    <Typography.Text
                        ellipsis
                        style={{
                            display: 'block',
                            maxWidth: 620,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {value || '-'}
                    </Typography.Text>
                </Tooltip>
            ),
        },
        {
            title: 'Brand',
            dataIndex: ['input', 'brand'],
            key: 'brand',
            width: 120,
            ...brandFilterProps
        },
        {
            title: 'Generation Status',
            dataIndex: 'generationStatus',
            key: 'generationStatus',
            width: 150,
            filters: generationStatusFilters,
            filterMultiple: false,
            sorter: (a, b) => (a.generationStatus || '').localeCompare(b.generationStatus || ''),
            render: (status: GenerationStatus) => (
                <Tag color={statusColorGeneration[status]}>
                    {status}
                </Tag>
            )
        },
        {
            title: 'Input Completeness',
            key: 'inputCompleteness',
            width: 145,
            align: 'center',
            render: (_, product) => {
                const completeness = getInputCompleteness(product.input);
                const complete = completeness.filled === completeness.total;
                const tooltip = complete
                    ? 'All core input fields are filled'
                    : `Missing: ${completeness.missing.join(', ')}`;

                return (
                    <Tooltip title={tooltip}>
                        <Tag color={complete ? 'success' : 'warning'}>
                            {completeness.filled}/{completeness.total}
                        </Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Keywords',
            dataIndex: 'hasKeywords',
            key: 'hasKeywords',
            width: 100,
            align: 'center',
            filters: [
                { text: 'Assigned', value: true },
                { text: 'Missing', value: false },
            ],
            filterMultiple: false,
            render: (_, product) => {
                const keywordCount = product.keywordCount ?? getKeywordCount(product.input);
                const assigned = product.hasKeywords ?? keywordCount > 0;

                return (
                    <Tooltip title={assigned ? `${keywordCount} keywords assigned` : 'No keywords assigned'}>
                        <Tag color={assigned ? 'success' : 'default'}>
                            {assigned ? 'Yes' : 'No'}
                        </Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Updated At',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 150,
            sorter: (a, b) => timestampMs(a.updatedAt) - timestampMs(b.updatedAt),
            render: formatDateTime,
        },
    ];
